import React from 'react';
import { Mail, MapPin, Phone, Facebook, Twitter, Linkedin, Instagram, ChevronRight, Award, Shield, Zap } from 'lucide-react';
import JR_Logo from "../assets/logo.png";
import { Link } from 'react-router-dom';

const Footer = () => {
    const quickLinks = [
        { name: 'Home', path: '/' },
        { name: 'About Us', path: '/about' },
        { name: 'Products', path: '/products' },
        { name: 'Quality & Services', path: '/quality-services' },
        { name: 'Infrastructure', path: '/infrastructure' },
        { name: 'Contact', path: '/contact' },
    ];

    const products = [
        { name: 'Refractory Bricks', path: '/products/refractory_bricks' },
        { name: 'Castable Monolithics', path: '/products/castable-monolithics' },
        { name: 'Mortar', path: '/products/mortar' },
        { name: 'Insulation Fiber Blanket', path: '/products/insulation-fiber-blanket' },
        { name: 'Bauxite', path: '/products/bauxite' },
        { name: 'Refractory Cement', path: '/products/refractory-cement' },
    ];

    const industries = [
        'Steel Industry',
        'Cement Industry',
        'Glass Industry',
        'Ceramic Industry',
        'Power Plants',
        'Petrochemical',
    ];

    return (
        <footer className="bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white">
            {/* Main Footer Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">

                    {/* Company Info */}
                    <div className="lg:col-span-1">
                        <div className="w-40 h-14 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center shadow-lg mb-6">
                            <div className="w-40 h-14 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center shadow-lg">
                                <Link to="/">
                                    <img
                                        src={JR_Logo}
                                        alt="Janta Refractory Logo"
                                        className="w-40 h-16 object-contain mt-3"
                                    />
                                </Link>
                            </div>
                        </div>
                        <p className="text-gray-400 mb-6 leading-relaxed">
                            Leading manufacturer of premium quality refractories in India since 1998. Trusted by industries nationwide for excellence and reliability.
                        </p>

                        {/* Key Features */}
                        <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm text-gray-300">
                                <Award className="w-4 h-4 text-orange-500" />
                                <span>ISO Certified</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-300">
                                <Shield className="w-4 h-4 text-orange-500" />
                                <span>Quality Assured</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-300">
                                <Zap className="w-4 h-4 text-orange-500" />
                                <span>30+ Years Experience</span>
                            </div>
                        </div>

                        {/* Social Media */}
                        <div className="flex gap-3 mt-6">
                            <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-orange-500 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110">
                                <Facebook className="w-5 h-5" />
                            </a>
                            <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-orange-500 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110">
                                <Twitter className="w-5 h-5" />
                            </a>
                            <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-orange-500 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110">
                                <Linkedin className="w-5 h-5" />
                            </a>
                            <a href="#" className="w-10 h-10 bg-gray-800 hover:bg-orange-500 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110">
                                <Instagram className="w-5 h-5" />
                            </a>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="text-lg font-semibold mb-6 text-white relative inline-block">
                            Quick Links
                            <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-orange-500 to-yellow-500"></span>
                        </h4>
                        <ul className="space-y-3">
                            {quickLinks.map((link) => (
                                <li key={link.name}>
                                    <a
                                        href={link.path}
                                        className="text-gray-400 hover:text-orange-500 transition-colors duration-300 flex items-center gap-2 group"
                                    >
                                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                                        {link.name}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Products & Industries */}
                    <div>
                        <h4 className="text-lg font-semibold mb-6 text-white relative inline-block">
                            Our Products
                            <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-gradient-to-r from-orange-500 to-yellow-500"></span>
                        </h4>
                        <ul className="space-y-3 mb-8">
                            {products.slice(0, 4).map((product) => (
                                <li key={product.name}>
                                    <a
                                        href={product.path}
                                        className="text-gray-400 hover:text-orange-500 transition-colors duration-300 flex items-center gap-2 group text-sm"
                                    >
                                        <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                                        {product.name}
                                    </a>
                                </li>
                            ))}
                        </ul>

                        <h4 className="text-lg font-semibold mb-4 text-white">Industries We Serve</h4>
                        <ul className="space-y-2">
                            {industries.slice(0, 4).map((industry) => (
                                <li key={industry} className="text-gray-400 text-sm flex items-center gap-2">
                                    <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                                    {industry}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-gray-700 bg-gray-950">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
                        {/* Copyright */}
                        <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-sm">
                            <p className="text-gray-400 text-center sm:text-left">
                                &copy; {new Date().getFullYear()} <span className="text-orange-500 font-semibold">JANTA REFRACTORY</span>. All rights reserved.
                            </p>
                            <span className="hidden sm:inline text-gray-700">|</span>
                            <p className="text-gray-400 flex items-center gap-2">
                                <span className="text-gray-500">Developed by</span>
                                <a
                                    href="https://onehexatech.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-orange-500 font-semibold hover:text-orange-400 transition-colors duration-300 flex items-center gap-1 group"
                                >
                                    OneHexa Tech
                                    <svg className="w-3 h-3 group-hover:translate-x-0.5 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                </a>
                            </p>
                        </div>

                        {/* Links */}
                        <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm">
                            <a href="#" className="text-gray-400 hover:text-orange-500 transition-colors duration-300 relative group">
                                Privacy Policy
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-500 group-hover:w-full transition-all duration-300"></span>
                            </a>
                            <span className="text-gray-700">•</span>
                            <a href="#" className="text-gray-400 hover:text-orange-500 transition-colors duration-300 relative group">
                                Terms of Service
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-500 group-hover:w-full transition-all duration-300"></span>
                            </a>
                            <span className="text-gray-700">•</span>
                            <a href="#" className="text-gray-400 hover:text-orange-500 transition-colors duration-300 relative group">
                                Sitemap
                                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-orange-500 group-hover:w-full transition-all duration-300"></span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;